import { FC, useContext, useEffect, useMemo, useState } from 'react';
import Tabs from 'components/ui/Tabs/Tabs';
import SearchFlight from 'components/flight/Search/SearchFlight';
import SearchTours from 'components/package/Search/SearchTours';
import SearchPackages from 'components/package/Search/SearchPackages';
import SearchHotel from 'components/hotel/Search/SearchHotel';
import SearchTransfer from 'components/transfer/Search/SearchTransfer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBed,
  faCar,
  faPlane,
  faPlaneLock,
  faSuitcase,
  faTicket,
  faTrain,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { SearchBoxTabs } from '../constants/generic';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import SearchDynamicFH from 'components/dynamic/Search/SearchDynamicFH';
import { MainContext } from 'contexts/MainContext';

interface MultiSearchBoxProps {
  config?: string;
}

const MultiSearchBox: FC<MultiSearchBoxProps> = (props) => {
  const { t } = useTranslation();
  const { initSiteData } = useContext(MainContext);
  const [activeTab, setActiveTab] = useState<SearchBoxTabs>();

  const [defaultConfig, setDefaultConfig] = useState('HTL');
  const config = props.config?.toUpperCase() || defaultConfig;
  const tabsDef = config.split('$');

  const tabs = tabsDef?.[0].split('_') as SearchBoxTabs[];
  const defaultTab = tabsDef?.[1];

  useEffect(() => {
    const defaultTabIndex = tabs.indexOf(defaultTab as SearchBoxTabs);
    if (defaultTabIndex === -1) {
      setActiveTab(tabs[0]);
    } else {
      setActiveTab(tabs[defaultTabIndex]);
    }

    if (initSiteData?.other?.flightsAvailbale === 'true') {
      setDefaultConfig((prev) => prev + '_FLT_FLH');
    }
    if (initSiteData?.other?.packagesAvailable === 'true') {
      setDefaultConfig((prev) => prev + '_PAC');
    }
    setDefaultConfig((prev) => prev + '_TRA');
  }, []); // eslint-disable-line

  const isVisible = (tab: SearchBoxTabs) => tabs.includes(tab);

  const tabItems = useMemo(() => {
    const defs = [
      {
        tab: SearchBoxTabs.flight,
        label: (
          <>
            <FontAwesomeIcon icon={faPlane as any} />
            {t('flight.s')}
          </>
        ),
      },
      {
        tab: SearchBoxTabs.package,
        label: (
          <>
            <FontAwesomeIcon icon={faSuitcase as any} />
            {t('package.s')}
          </>
        ),
      },
      {
        tab: SearchBoxTabs.tours,
        label: (
          <>
            <FontAwesomeIcon icon={faTrain as any} />
            {t('package.tours')}
          </>
        ),
      },
      {
        tab: SearchBoxTabs.hotel,
        label: (
          <>
            <FontAwesomeIcon icon={faBed as any} />
            {t('hotel.s')}
          </>
        ),
      },
      {
        tab: SearchBoxTabs.transfer,
        label: (
          <>
            <FontAwesomeIcon icon={faCar as any} />
            {t('transfer.s')}
          </>
        ),
      },
      {
        tab: SearchBoxTabs.dynamicFH,
        label: (
          <>
            <FontAwesomeIcon icon={faPlaneLock as any} />
            {t('dyn.fl_htl')}
          </>
        ),
      },
      {
        tab: SearchBoxTabs.dynamicFHS,
        label: (
          <>
            <FontAwesomeIcon icon={faTicket as any} />
            {t('dyn.dynamic')}
          </>
        ),
      },
    ];

    return defs
      .filter(({ tab }) => tabs.includes(tab))
      .sort((a, b) => tabs.indexOf(a.tab) - tabs.indexOf(b.tab))
      .map(({ tab, label }) => ({ value: tab, label }));
  }, [tabs, t]);

  return (
    <div className="main-search-box">
      {!!activeTab && (
        <Tabs
          label={t('search.search')}
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as SearchBoxTabs)}
          items={tabItems}
        />
      )}
      {activeTab === SearchBoxTabs.flight && isVisible(SearchBoxTabs.flight) && (
        <SearchFlight loadingSearch={false} onClickStop={() => {}} />
      )}
      {activeTab === SearchBoxTabs.package && isVisible(SearchBoxTabs.package) && (
        <SearchPackages />
      )}
      {activeTab === SearchBoxTabs.tours && isVisible(SearchBoxTabs.tours) && <SearchTours />}
      {activeTab === SearchBoxTabs.hotel && isVisible(SearchBoxTabs.hotel) && (
        <SearchHotel loadingSearch={false} onClickStop={() => {}} />
      )}
      {activeTab === SearchBoxTabs.transfer && isVisible(SearchBoxTabs.transfer) && (
        <SearchTransfer />
      )}
      {activeTab === SearchBoxTabs.dynamicFH && isVisible(SearchBoxTabs.dynamicFH) && (
        <SearchDynamicFH matrixConfig="Dynamic_FMHM" />
      )}
    </div>
  );
};

export default withErrorBoundary(MultiSearchBox, 'MultiSearchBox');
